import { v4 as uuidv4 } from "uuid";
import CryptoJS from "crypto-js";
import { Kit } from "@src/redux/index";
import { startCase } from "lodash";
import { ApolloClient, gql, HttpLink, InMemoryCache } from "@apollo/client";

// Helper functions for encryption and decryption
export const encryptData = (data: string, salt: string): string =>
CryptoJS.AES.encrypt(data, salt).toString();

export const decryptData = (
cipherText: string | null,
salt: string)
: string | null => {
  if (!cipherText) return null;
  const bytes = CryptoJS.AES.decrypt(cipherText, salt);
  try {
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
};

// Store item in local storage
export function storeItem(key: string, data: any): void {
  const salt = process.env.REACT_APP_SALT || "";
  const value = JSON.stringify(data);
  const encryptedKey = window.btoa(key);
  const encryptedValue = encryptData(value, salt);
  window.localStorage.setItem(encryptedKey, encryptedValue);
  window.dispatchEvent(new Event(`${key}`));
}

// Retrieve item from local storage
export function retrieveItem(key: string): any | null {
  const salt = process.env.REACT_APP_SALT || "";
  try {
    const encryptedKey = window.btoa(key);
    const value = window.localStorage.getItem(encryptedKey);
    const decryptedValue = decryptData(value, salt);
    return decryptedValue ? JSON.parse(decryptedValue) : null;
  } catch {
    return null;
  }
}

// Clear all items in local storage
export function clearLocalStorage(): void {
  try {
    window.localStorage.clear();
  } catch {

    // Handle errors if needed
  }}

// export function get_uuid(): string {
//   return uuidv4();
// }
export function getUuid(buf: number = 0): string {
  const now: Date = new Date();
  const time: number = now.getTime();
  let offset: number = now.getTimezoneOffset();
  offset = offset * 60000;
  let dt: string = `${time - offset + buf}`;
  const uuid: string = `${dt.substr(0, 8)}-${dt.substr(8, 4)}-${dt.substr(
    12,
    1
  )}xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, function (c) {
    const r: number = (parseInt(dt, 10) + Math.random() * 16) % 16 | 0;
    dt = Math.floor(parseInt(dt, 10) / 16).toString();
    return (c === "x" ? r : r & 0x3 | 0x8).toString(16);
  });
  return uuid;
}

export function getKitLabel(label: string, data: Kit): string | null {
  const deviceLabels: {[key: string]: {field: keyof Kit;name: string;};} = {
    maximumVincenseDevices: {
      field: "maximumVincenseDevices",
      name: "VinCense"
    },
    maximumEcgDevices: { field: "maximumEcgDevices", name: "ECG" },
    maximumGlucometerDevices: {
      field: "maximumGlucometerDevices",
      name: "Glucometer"
    },
    maximumWeighingScaleDevices: {
      field: "maximumWeighingScaleDevices",
      name: "Weighing Scale"
    },
    maximumBpMonitorDevices: {
      field: "maximumBpMonitorDevices",
      name: "BP Monitor"
    }
  };

  if (deviceLabels[label]) {
    const { field, name } = deviceLabels[label];
    const count = data[field];

    return `${name} (Max ${count} device${count === 1 ? "" : "s"})`;
  }

  return null;
}

export function getKitLabelWithoutCount(
label: string,
data: Kit)
: string | null {
  const deviceLabels: {[key: string]: {field: keyof Kit;name: string;};} = {
    maximumVincenseDevices: {
      field: "maximumVincenseDevices",
      name: "VinCense"
    },
    maximumEcgDevices: { field: "maximumEcgDevices", name: "ECG" },
    maximumGlucometerDevices: {
      field: "maximumGlucometerDevices",
      name: "Glucometer"
    },
    maximumWeighingScaleDevices: {
      field: "maximumWeighingScaleDevices",
      name: "Weighing Scale"
    },
    maximumBpMonitorDevices: {
      field: "maximumBpMonitorDevices",
      name: "BP Monitor"
    }
  };

  if (deviceLabels[label]) {
    const { name } = deviceLabels[label];

    return `${name}`;
  }

  return null;
}

export const exportReadingListQuery = gql`
  query exportReadingList(
    $filter: readingFilterInput!
    $data: emailIdListInput!
  ) {
    exportReadingList(filter: $filter, data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

export const createAwsApolloClient = (isAws) => {
  const sessionId = retrieveItem("sessionId") ?? retrieveItem("sessionId");
  return new ApolloClient({
    link: new HttpLink({
      uri: isAws ?
      "http://192.168.1.18:6227/graphql" :
      "http://192.168.1.11:6227/graphql",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionId ?? ""
      }
    }),
    cache: new InMemoryCache()
  });
};

export const parsePrismaError = (error: any) => {
  if (!error) return null;

  // Handle GraphQL error structure with errors array
  if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
    const firstError = error.errors[0];
    const message = firstError.message || "";

    // Unique constraint violation
    if (
    message.includes("Unique constraint failed") ||
    firstError.extensions?.exception?.code === "P2002")
    {
      const fieldMatch = message.match(
        /Unique constraint failed on the fields: \(`([^`]+)`,`([^`]+)`\)/
      );
      if (fieldMatch) {
        const [, field1, field2] = fieldMatch;
        if (
        field1 === "customTestGroupId" && field2 === "name" ||
        field1 === "name" && field2 === "customTestGroupId")
        {
          return "Username already exists in this organization.";
        }
      }
      return "This name is already in use.";
    }

    // Prisma error codes
    const errorCode = firstError.extensions?.exception?.code;
    switch (errorCode) {
      case "P2003":
        return "Foreign key constraint failed.";
      case "P2025":
        return "Record not found.";
      case "P2000":
        return "Validation error.";
      case "P1001":
        return "Database connection error.";
      default:
        break;
    }
  }

  // Handle direct error message
  if (error.message) {
    const message = error.message;

    if (message.includes("Unique constraint failed") || message.includes("P2002")) {
      const fieldMatch = message.match(
        /Unique constraint failed on the fields: \(`([^`]+)`,`([^`]+)`\)/
      );
      if (fieldMatch) {
        const [, field1, field2] = fieldMatch;
        if (
        field1 === "customTestGroupId" && field2 === "name" ||
        field1 === "name" && field2 === "customTestGroupId")
        {
          return "Username already exists in this organization.";
        }
      }
      return "This name is already in use.";
    }

    if (message.includes("P2003")) return "Foreign key constraint failed.";
    if (message.includes("P2025")) return "Record not found.";
    if (message.includes("P2000")) return "Validation error.";
    if (message.includes("P1001")) return "Database connection error.";
  }

  return null;
};